import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { formatDateTime } from '@/lib/i18n';
import { api } from '@/lib/api';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TD, TH, THead, TR, Table, TableWrap } from '@/components/ui/table';
import {
  ContactChannel,
  apiError,
  apiStatus,
  type Segment,
  type SegmentContactableResult,
  type SegmentMembersPage,
} from '../lib';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'neverEvaluated' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; page: SegmentMembersPage };

/**
 * Who is in this segment, from the materialised table (ADR-0010 §3.2).
 *
 * `neverEvaluated` is its OWN state, not folded into `error` or `ready` with
 * zero rows — the backend answers a 409 for exactly that reason: "nobody
 * matches" and "nothing has run yet" must not render the same, and a 409 is
 * the one response this screen must not treat as a generic failure.
 */
export default function SegmentMembersDialog({
  segment,
  onClose,
  onChanged,
}: {
  segment: Segment;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const canEvaluate = can('segments.evaluate');

  const [contactableOn, setContactableOn] = useState<ContactChannel | ''>('');
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [evaluating, setEvaluating] = useState(false);
  const [contactable, setContactable] = useState<SegmentContactableResult | null>(null);

  const load = useCallback(() => {
    setState({ kind: 'loading' });
    api
      .get<SegmentMembersPage>(`/segments/${segment.id}/members`, {
        params: { limit: 50, contactableOn: contactableOn || undefined },
      })
      .then(({ data }) => setState({ kind: 'ready', page: data }))
      .catch((e) => {
        if (apiStatus(e) === 409) setState({ kind: 'neverEvaluated' });
        else setState({ kind: 'error', message: apiError(e, t('crm.segments.membersLoadFailed')) });
      });
  }, [segment.id, contactableOn, t]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!segment.lastEvaluatedAt) {
      setContactable(null);
      return;
    }
    api
      .get<SegmentContactableResult>(`/segments/${segment.id}/contactable`, {
        params: { channel: ContactChannel.SMS },
      })
      .then(({ data }) => setContactable(data))
      .catch(() => setContactable(null));
  }, [segment.id, segment.lastEvaluatedAt]);

  const evaluate = async () => {
    setEvaluating(true);
    try {
      await api.post(`/segments/${segment.id}/evaluate`);
      toast.success(t('crm.segments.evaluated'));
      onChanged();
      load();
    } catch (e) {
      toast.error(apiError(e, t('crm.segments.evaluateFailed')));
    } finally {
      setEvaluating(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={segment.name}
      className="max-w-3xl"
      footer={
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <FreshnessBanner segment={segment} locale={i18n.language} />
          {canEvaluate && (
            <Button size="sm" variant="outline" onClick={() => void evaluate()} disabled={evaluating}>
              <Icon as={RefreshCw} size="sm" className="me-1.5" />
              {t('crm.segments.evaluateNow')}
            </Button>
          )}
        </div>

        {/* Membership carries no consent claim (ADR-0010 §4) — this is the
            explicit, opt-in intersection, never applied by default. */}
        <Field
          label={t('crm.segments.contactableFilter')}
          hint={t('crm.segments.contactableFilterHint')}
        >
          <Select
            value={contactableOn}
            onChange={(e) => setContactableOn(e.target.value as ContactChannel | '')}
          >
            <option value="">{t('crm.segments.everyMember')}</option>
            {Object.values(ContactChannel).map((c) => (
              <option key={c} value={c}>
                {t('crm.segments.contactableOn', { channel: t(`crm.consent.channel.${c}`) })}
              </option>
            ))}
          </Select>
        </Field>

        {contactable && (
          <p className="text-sm text-ink-muted">
            {t('crm.segments.contactableSummary', {
              contactable: contactable.contactableCount,
              total: contactable.memberCount,
              channel: t(`crm.consent.channel.${ContactChannel.SMS}`),
            })}
          </p>
        )}

        {state.kind === 'loading' && <LoadingState label={t('common.loading')} />}

        {state.kind === 'neverEvaluated' && (
          <EmptyState
            title={t('crm.segments.neverEvaluatedTitle')}
            hint={t('crm.segments.neverEvaluatedHint')}
            action={
              canEvaluate ? (
                <Button size="sm" onClick={() => void evaluate()} disabled={evaluating}>
                  {t('crm.segments.evaluateNow')}
                </Button>
              ) : undefined
            }
          />
        )}

        {state.kind === 'error' && <ErrorState message={state.message} onRetry={load} />}

        {state.kind === 'ready' && state.page.data.length === 0 && (
          <EmptyState
            title={t('crm.segments.noMembersTitle')}
            hint={t('crm.segments.noMembersHint')}
          />
        )}

        {state.kind === 'ready' && state.page.data.length > 0 && (
          <TableWrap>
            <Table>
              <THead>
                <TR>
                  <TH>{t('crm.customers.name')}</TH>
                  <TH>{t('crm.customers.phone')}</TH>
                  <TH>{t('crm.customers.email')}</TH>
                </TR>
              </THead>
              <tbody>
                {state.page.data.map((member) => (
                  <TR key={member.id}>
                    <TD className="font-medium">{member.name ?? t('crm.customers.unnamed')}</TD>
                    <TD dir="ltr" className="text-start">{member.phone ?? '—'}</TD>
                    <TD>{member.email ?? '—'}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        {state.kind === 'ready' && (
          <p className="text-xs text-ink-muted">
            {t('crm.segments.membersTotal', { count: state.page.meta.total })}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function FreshnessBanner({ segment, locale }: { segment: Segment; locale: string }) {
  const { t } = useTranslation();
  if (segment.neverEvaluated) {
    return <Badge tone="neutral">{t('crm.segments.neverEvaluatedBadge')}</Badge>;
  }
  return (
    <div className="flex items-center gap-2 text-sm">
      <Badge tone={segment.stale ? 'warning' : 'success'}>
        {t(segment.stale ? 'crm.segments.stale' : 'crm.segments.fresh')}
      </Badge>
      <span className="text-ink-muted">
        {t('crm.segments.evaluatedAt', {
          date: formatDateTime(segment.lastEvaluatedAt!, locale),
        })}
      </span>
    </div>
  );
}
